import {
  ScatterChart, Scatter, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

interface AnalyticsEvent {
  campaignId: string
  subscriberEmail: string
  eventType: 'open' | 'click'
  timestamp: string
}

interface RFMSegmentationTabProps {
  analytics: AnalyticsEvent[]
}

export default function RFMSegmentationTab({ analytics }: RFMSegmentationTabProps) {
  const getRFMSegments = () => {
    const subscriberActivity: Record<string, { recency: number; frequency: number; engagement: number }> = {}

    analytics.forEach(event => {
      const email = event.subscriberEmail
      if (!subscriberActivity[email]) {
        subscriberActivity[email] = { recency: 0, frequency: 0, engagement: 0 }
      }

      const daysSince = Math.floor((new Date().getTime() - new Date(event.timestamp).getTime()) / (24 * 60 * 60 * 1000))
      subscriberActivity[email].recency = Math.max(subscriberActivity[email].recency, daysSince)
      subscriberActivity[email].frequency += 1
      subscriberActivity[email].engagement += event.eventType === 'click' ? 2 : 1
    })

    const rfmData = Object.entries(subscriberActivity).map(([email, data]) => {
      let segment = 'At Risk'
      if (data.recency <= 7 && data.frequency >= 5 && data.engagement >= 10) segment = 'Champions'
      else if (data.recency <= 14 && data.frequency >= 3) segment = 'Loyal'
      else if (data.recency <= 30 && data.engagement >= 5) segment = 'Potential'
      else if (data.recency > 60) segment = 'Lost'
      
      return { email, ...data, segment }
    })

    const segmentCounts: Record<string, { count: number; totalRecency: number; totalFrequency: number; totalEngagement: number }> = {}
    rfmData.forEach(d => {
      if (!segmentCounts[d.segment]) {
        segmentCounts[d.segment] = { count: 0, totalRecency: 0, totalFrequency: 0, totalEngagement: 0 }
      }
      segmentCounts[d.segment].count++
      segmentCounts[d.segment].totalRecency += d.recency
      segmentCounts[d.segment].totalFrequency += d.frequency
      segmentCounts[d.segment].totalEngagement += d.engagement
    })

    return Object.entries(segmentCounts).map(([segment, data]) => ({
      segment,
      count: data.count,
      percentage: ((data.count / rfmData.length) * 100).toFixed(1),
      avgRecency: Math.round(data.totalRecency / data.count),
      avgFrequency: Math.round(data.totalFrequency / data.count),
      avgMonetary: Math.round(data.totalEngagement / data.count)
    }))
  }

  return (
    <div className="space-y-8">
      {/* RFM Segments Grid */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">RFM Subscriber Segments</h2>
        <p className="text-sm text-gray-600 mb-4">Recency, Frequency, Monetary value analysis</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {getRFMSegments().map((segment, idx) => (
            <div key={idx} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{segment.segment}</h3>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  segment.segment.includes('Champions') ? 'bg-green-100 text-green-800' :
                  segment.segment.includes('At Risk') ? 'bg-red-100 text-red-800' :
                  segment.segment.includes('Loyal') ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {segment.count} subscribers
                </span>
              </div>
              <div className="space-y-1 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Recency:</span>
                  <span className="font-medium">{segment.avgRecency} days</span>
                </div>
                <div className="flex justify-between">
                  <span>Frequency:</span>
                  <span className="font-medium">{segment.avgFrequency} opens</span>
                </div>
                <div className="flex justify-between">
                  <span>Engagement:</span>
                  <span className="font-medium">{segment.avgMonetary}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <ScatterChart>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="avgRecency" name="Recency" label={{ value: 'Days Since Last Open', position: 'bottom' }} />
            <YAxis dataKey="avgFrequency" name="Frequency" label={{ value: 'Total Opens', angle: -90, position: 'left' }} />
            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
            <Scatter data={getRFMSegments()} fill="#10b981">
              {getRFMSegments().map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Scatter>
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}
